import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { APIError, queryUtils, useCreatePdpMutation, useSearchPdpsQuery } from '@vizit/api';
import type { Region, Retailer } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { RetailerKey } from '@vizit/constants';
import type { ScorePDPSubmitData } from '@vizit/organisms';
import * as React from 'react';
import { isRegion, isRetailer } from '../../utils/retailer-region-guards';

enum ProductImportErrorType {
  None = 'none',
  Duplicate = 'duplicate',
  UnsupportedCategory = 'unsupported-category',
  ClassificationFailed = 'classification-failed',
  Generic = 'generic',
}

const UNSUPPORTED_CATEGORY_ERROR_CODES = ['CATEGORY_NOT_IN_VIZIT_DB', 'CATEGORY_NOT_IN_ORG_ICP'];
const CLASSIFICATION_FAILED_ERROR_CODE = 'CLASSIFICATION_FAILED';

interface ParsedAPIError {
  error_code?: string;
}

const parseAPIErrorBody = (error: APIError): ParsedAPIError => {
  try {
    const parsed: unknown = JSON.parse(error.message);
    if (parsed && typeof parsed === 'object' && 'error_code' in parsed) {
      return { error_code: String(parsed.error_code) };
    }
    return {};
  } catch {
    return {};
  }
};

interface UseProductImportDialogProps {
  workspaceId?: string | null;
  retailer?: Retailer;
  region?: Region;
  onSuccessfulImport: (pdpId: string) => void;
  onImportError?: (error: unknown | APIError) => void;
}

interface UseProductImportDialogReturn {
  errorType: ProductImportErrorType;
  duplicatePdpId: string | null;
  onSubmit: (data: ScorePDPSubmitData) => Promise<void>;
  resetState: () => void;
}

const createImportParams = (workspaceId: string, itemId: string, retailer: Retailer, region: Region) => ({
  workspace_id: workspaceId,
  item_id: itemId,
  region,
  retailer,
});

// RetailerKey is `<retailer>_<region>` (e.g. 'walmart_us'); split into the API's
// separate retailer + region fields so a Walmart input isn't sent as Amazon.
const splitRetailerKey = (key: RetailerKey): { retailer: Retailer; region: Region } | null => {
  const [retailer, region] = key.split('_');
  if (retailer && region && isRetailer(retailer) && isRegion(region)) {
    return { retailer, region };
  }
  return null;
};

const findExistingPdp = async (workspaceId: string, itemId: string): Promise<string | null> => {
  try {
    const result = await useSearchPdpsQuery.fetcher({
      workspace_id: workspaceId,
      search_item_id: itemId,
      limit: 1,
      offset: 0,
    })();

    return result.data?.[0]?.id ?? null;
  } catch {
    return null;
  }
};

const logImportError = (
  error: unknown,
  workspaceId: string | null | undefined,
  itemId: string,
  retailer: Retailer,
  region: Region,
) => {
  const params = createImportParams(workspaceId ?? '', itemId, retailer, region);

  Sentry.addBreadcrumb({
    message: 'Product import failed.',
    level: 'error',
    data: params,
  });

  Sentry.captureException(error, {
    level: 'error',
    tags: {
      feature: SENTRY_FEATURE_TAGS.Product,
      stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productImport,
    },
    extra: {
      workspace_id: workspaceId,
      request_params: params,
    },
  });
};

const useProductImportDialog = (props: UseProductImportDialogProps): UseProductImportDialogReturn => {
  const { workspaceId, retailer, region, onSuccessfulImport, onImportError } = props;

  const queryClient = useQueryClient();
  const [errorType, setErrorType] = React.useState<ProductImportErrorType>(ProductImportErrorType.None);
  const [duplicatePdpId, setDuplicatePdpId] = React.useState<string | null>(null);

  const { mutateAsync } = useCreatePdpMutation();

  const resetState = React.useCallback(() => {
    setErrorType(ProductImportErrorType.None);
    setDuplicatePdpId(null);
  }, []);

  const handleOnSubmit = React.useCallback(
    async ({ product, retailerKey }: ScorePDPSubmitData) => {
      setErrorType(ProductImportErrorType.None);
      setDuplicatePdpId(null);

      const detected = retailerKey ? splitRetailerKey(retailerKey) : null;
      const effectiveRetailer = detected?.retailer ?? retailer;
      const effectiveRegion = detected?.region ?? region;

      if (!effectiveRetailer || !effectiveRegion) {
        const configError = new Error('Missing retailer/region configuration');
        setErrorType(ProductImportErrorType.Generic);
        onImportError?.(configError);
        return;
      }

      const params = createImportParams(workspaceId ?? '', product, effectiveRetailer, effectiveRegion);

      Sentry.addBreadcrumb({
        message: 'Starting product import.',
        level: 'info',
        data: params,
      });

      try {
        const responseData = await mutateAsync(params);

        if (responseData?.id) {
          Sentry.addBreadcrumb({
            message: 'Product imported successfully.',
            level: 'info',
            data: { pdp_id: responseData.id, ...params },
          });

          // Invalidate PDP list queries so newly imported PDP appears in search results
          await queryUtils.invalidateAllPdpLists(queryClient, workspaceId ?? undefined);

          onSuccessfulImport(responseData.id);
        } else {
          throw new Error(`Failed to create PDP ID: ${JSON.stringify(params)}`);
        }
      } catch (error) {
        if (error instanceof APIError && error.status === 409) {
          setErrorType(ProductImportErrorType.Duplicate);
          setDuplicatePdpId(workspaceId ? await findExistingPdp(workspaceId, product) : null);

          Sentry.addBreadcrumb({
            message: 'Duplicate product import attempted.',
            level: 'info',
            data: params,
          });
        } else {
          const errorCode = error instanceof APIError ? parseAPIErrorBody(error).error_code : undefined;

          if (errorCode && UNSUPPORTED_CATEGORY_ERROR_CODES.includes(errorCode)) {
            setErrorType(ProductImportErrorType.UnsupportedCategory);
          } else if (errorCode === CLASSIFICATION_FAILED_ERROR_CODE) {
            setErrorType(ProductImportErrorType.ClassificationFailed);
          } else {
            setErrorType(ProductImportErrorType.Generic);
          }
          logImportError(error, workspaceId, product, effectiveRetailer, effectiveRegion);
        }

        onImportError?.(error);
      }
    },
    [workspaceId, retailer, region, mutateAsync, queryClient, onSuccessfulImport, onImportError],
  );

  return {
    errorType,
    duplicatePdpId,
    onSubmit: handleOnSubmit,
    resetState,
  };
};

export { useProductImportDialog, ProductImportErrorType };
export type { UseProductImportDialogProps };
